import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Section, Toggle } from '../PageShell.tsx';
import { EmptyState, Spinner } from '../ui.tsx';
import { TrashIcon } from '../icons.tsx';
import { ApiRequestError, api } from '../../lib/api.ts';
import { SaveRow, useSaved } from './shared.tsx';
import type { SettingsDto } from './shared.tsx';

interface BlacklistRow {
  id: number;
  key: string;
  value: string;
}

export function RetentionTab({ settings }: { settings: SettingsDto }): React.JSX.Element {
  const queryClient = useQueryClient();

  const [archiving, setArchiving] = useState(
    settings.chat.archiving ?? { days: 30, ongoingChats: false, newChats: false },
  );
  const [saved, markSaved] = useSaved();
  const [error, setError] = useState<string | null>(null);

  // Clearing the field makes `Number('')` zero, which the server rejects. The
  // Save button is a plain button outside any form, so the input's `min` never
  // triggers browser validation — check it here instead of letting the request
  // 400 with nothing shown to the user.
  const daysInvalid =
    !Number.isInteger(archiving.days) || archiving.days < 1 || archiving.days > 3650;

  const blacklist = useQuery({
    queryKey: ['settings', 'blacklist'],
    queryFn: () => api.get<{ items: BlacklistRow[] }>('/settings/blacklist'),
  });

  const save = useMutation({
    mutationFn: () => api.put('/settings/archiving', archiving),
    onSuccess: () => {
      setError(null);
      markSaved();
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong');
    },
  });

  const removeEntry = useMutation({
    mutationFn: (id: number) => api.delete(`/settings/blacklist/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'blacklist'] });
    },
  });

  return (
    <>
      <Section
        title="Auto-archiving"
        description="Quiet conversations get archived and returned to the pool."
      >
        <div className="max-w-xs">
          <label className="lg-label" htmlFor="archiving-days">
            Archive after (days)
          </label>
          <input
            id="archiving-days"
            type="number"
            min={1}
            max={3650}
            className="lg-input"
            value={archiving.days}
            onChange={(event) => {
              setArchiving((previous) => ({ ...previous, days: Number(event.target.value) }));
            }}
          />
        </div>

        <div className="mt-4 space-y-3">
          <Toggle
            label="Archive unassigned conversations"
            checked={archiving.newChats}
            onChange={(value) => {
              setArchiving((previous) => ({ ...previous, newChats: value }));
            }}
          />
          <Toggle
            label="Archive assigned conversations"
            checked={archiving.ongoingChats}
            onChange={(value) => {
              setArchiving((previous) => ({ ...previous, ongoingChats: value }));
            }}
          />
        </div>

        <SaveRow
          pending={save.isPending}
          saved={saved}
          disabled={daysInvalid}
          error={error ?? (daysInvalid ? 'Enter a number of days between 1 and 3650.' : null)}
          onSave={() => {
            save.mutate();
          }}
        />
      </Section>

      <Section
        title="Blocked customers"
        description="Anyone here is flagged as spam automatically on their next message."
      >
        {blacklist.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : (blacklist.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nobody is blocked" />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {(blacklist.data?.items ?? []).map((entry) => (
              <li key={entry.id} className="flex items-center gap-3 py-2.5">
                <span className="lg-badge bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {entry.key}
                </span>
                <span className="flex-1 truncate text-sm text-slate-800 dark:text-slate-100">
                  {entry.value}
                </span>
                <button
                  type="button"
                  className="lg-btn-ghost px-2"
                  title="Unblock"
                  onClick={() => {
                    removeEntry.mutate(entry.id);
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="sr-only">Unblock</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
